import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import type { ForecastAccount } from '../../types';

interface SearchableAccount extends ForecastAccount {
  sectionType: 'Revenue' | 'Expense';
}

interface AccountSearchBarProps {
  revenueAccounts: ForecastAccount[];
  expenseAccounts: ForecastAccount[];
  expandedSections: Record<string, boolean>;
  onEnsureExpanded: (sectionId: string) => void;
  onSelectAccount: (account: ForecastAccount) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  iconOnly?: boolean;
}

export function AccountSearchBar({
  revenueAccounts,
  expenseAccounts,
  expandedSections,
  onEnsureExpanded,
  onSelectAccount,
  scrollContainerRef,
  iconOnly = false,
}: AccountSearchBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const allAccounts = useMemo<SearchableAccount[]>(() => [
    ...revenueAccounts.map(a => ({ ...a, sectionType: 'Revenue' as const })),
    ...expenseAccounts.map(a => ({ ...a, sectionType: 'Expense' as const })),
  ], [revenueAccounts, expenseAccounts]);

  const results = useMemo<SearchableAccount[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allAccounts
      .filter(a =>
        a.accountName.toLowerCase().includes(q) ||
        a.accountNumber.toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [allAccounts, query]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  const handleSelectResult = useCallback((account: SearchableAccount) => {
    const section = account.sectionType === 'Revenue' ? 'revenue' : 'expense';
    const deptKey = `dept:${section}:${account.department}`;

    // Expand parent section and department if collapsed
    if (expandedSections[section] === false) {
      onEnsureExpanded(section);
    }
    if (expandedSections[deptKey] !== true) {
      onEnsureExpanded(deptKey);
    }

    // Open driver panel
    onSelectAccount(account);

    // Close search
    setIsOpen(false);
    setQuery('');

    // Capture page scroll now, before anything external moves it
    const savedPageScroll = window.scrollY;

    // Scroll to row after DOM updates
    setTimeout(() => {
      // Restore page scroll position first (panel mount may have shifted it)
      window.scrollTo({ top: savedPageScroll, behavior: 'instant' as ScrollBehavior });

      const container = scrollContainerRef.current;
      const row = container?.querySelector(`[data-account-id="${account.chartOfAccountId}"]`);
      if (container && row) {
        const rowEl = row as HTMLElement;
        const containerRect = container.getBoundingClientRect();
        const rowRect = rowEl.getBoundingClientRect();
        const scrollTarget = container.scrollTop + rowRect.top - containerRect.top - container.clientHeight / 2 + rowEl.clientHeight / 2;
        container.scrollTo({ top: scrollTarget, behavior: 'smooth' });
      }
    }, 80);
  }, [expandedSections, onEnsureExpanded, onSelectAccount, scrollContainerRef]);

  return (
    <div ref={containerRef} className="relative">
      {iconOnly ? (
        <button
          onClick={() => setIsOpen(v => !v)}
          className="p-1.5 rounded transition-colors"
          style={{ color: isOpen ? 'var(--lodge-orange-500)' : 'var(--lodge-onyx-400)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          title="Find account"
        >
          <Search size={15} />
        </button>
      ) : (
        <button
          onClick={() => setIsOpen(v => !v)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg transition-colors"
          style={isOpen
            ? { background: 'var(--lodge-orange-50)', border: '1px solid var(--lodge-orange-300)', color: 'var(--lodge-orange-600)' }
            : { border: '1px solid var(--lodge-onyx-200)', color: 'var(--lodge-onyx-600)' }}
          title="Find account"
        >
          <Search size={14} />
          <span>Find</span>
        </button>
      )}

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1 rounded-lg z-[200] overflow-hidden"
          style={{
            width: 320,
            background: 'var(--lodge-white)',
            border: '1px solid var(--lodge-onyx-100)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {/* Search input */}
          <div
            className="flex items-center gap-2 px-3 py-2"
            style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}
          >
            <Search size={14} style={{ color: 'var(--lodge-onyx-400)', flexShrink: 0 }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Account name or number…"
              className="flex-1 text-sm bg-transparent outline-none"
              style={{ color: 'var(--lodge-onyx-800)' }}
            />
            {query && (
              <button onClick={() => setQuery('')} style={{ color: 'var(--lodge-onyx-400)' }}>
                <X size={13} />
              </button>
            )}
          </div>

          {/* Results */}
          {query.trim() && (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {results.length === 0 ? (
                <p className="px-3 py-4 text-sm text-center" style={{ color: 'var(--lodge-onyx-400)' }}>
                  No accounts found
                </p>
              ) : (
                <ul>
                  {results.map(account => (
                    <li
                      key={account.chartOfAccountId}
                      onClick={() => handleSelectResult(account)}
                      className="flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors"
                      style={{ borderBottom: '1px solid var(--lodge-onyx-50)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-xs font-mono px-1.5 py-0.5 rounded"
                            style={{ background: 'var(--lodge-onyx-100)', color: 'var(--lodge-onyx-500)' }}
                          >
                            {account.accountNumber}
                          </span>
                          <span
                            className="text-xs px-1.5 py-0.5 rounded font-medium"
                            style={account.sectionType === 'Revenue'
                              ? { background: 'var(--color-info-bg)', color: 'var(--color-info-ink)' }
                              : { background: 'var(--lodge-orange-50)', color: 'var(--accent-ink)' }}
                          >
                            {account.sectionType}
                          </span>
                        </div>
                        <p
                          className="text-sm font-medium mt-1 truncate"
                          style={{ color: 'var(--lodge-onyx-800)' }}
                        >
                          {account.accountName}
                        </p>
                        <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--lodge-onyx-400)' }}>
                          {account.department}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!query.trim() && (
            <p className="px-3 py-4 text-sm text-center" style={{ color: 'var(--lodge-onyx-400)' }}>
              Type to search accounts
            </p>
          )}
        </div>
      )}
    </div>
  );
}
